import { formatDistanceToNow } from 'date-fns'
import { FileCode } from 'lucide-react'
import { Link } from 'react-router'
import type { Template } from '@/api/hooks/use-templates'
import { useTemplates } from '@/api/hooks/use-templates'

export function TemplateListPage(): React.JSX.Element {
  const { data, isLoading } = useTemplates()
  const templates = data?.data ?? []

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading templates...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">Templates</h1>
        <Link
          to="/templates/new"
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          + New Template
        </Link>
      </div>

      {templates.length === 0 ? (
        <EmptyTemplates />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      )}
    </div>
  )
}

function TemplateCard({ template }: { template: Template }): React.JSX.Element {
  return (
    <Link
      to={`/templates/${template.id}`}
      className="block rounded-xl border border-surface-3 bg-surface-0 p-4 transition-colors hover:border-brand-300 hover:bg-surface-1"
    >
      <h3 className="font-medium text-text-primary">{template.name}</h3>
      <p className="mt-1 text-sm text-text-secondary">v{template.version}</p>
      <div className="mt-3 text-xs text-text-tertiary">
        Updated {formatDistanceToNow(new Date(template.updated_at), { addSuffix: true })}
      </div>
    </Link>
  )
}

function EmptyTemplates(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileCode className="mb-4 h-12 w-12 text-text-tertiary" />
      <h3 className="text-lg font-medium text-text-primary">No templates yet</h3>
      <p className="mt-1 max-w-sm text-sm text-text-secondary">
        Create your first email template to start sending beautiful, consistent emails.
      </p>
      <Link
        to="/templates/new"
        className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Create your first template
      </Link>
    </div>
  )
}
